import styled from "styled-components";
import Image from "next/image";
import { breakpoints } from "@/styles/sizes";
import FACT_DISH_ICON from "/public/images/facts/fact_dish.svg";
import FACT_CARD_ICON from "/public/images/facts/fact_card.svg";
import FACT_STAR_ICON from "/public/images/facts/fact_star.svg";

const SectionWrapper = styled.section`
    padding: 50px;

    @media only screen and (${breakpoints.mobile}) {
        padding: 30px 20px;
    }
`;

const Heading = styled.h2`
    text-transform: uppercase;
    margin-top: 0;
    margin-bottom: 24px;
    color: #0e3325;
    font-weight: bold;
    text-align: center;
    font-size: 36px;
    font-family: "Josefin Sans";

    @media only screen and (${breakpoints.mobile}) {
        font-size: 25px;
        margin-bottom: 18px;
    }
`;

const FactsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;

    @media only screen and (${breakpoints.mobile}) {
        grid-template-columns: 1fr;
        gap: 15px;
    }
`;

const FactCard = styled.div`
    border: 2px solid #ebebe8;
    border-radius: 8px;
    padding: 25px 15px;
    text-align: center;
`;

const IconWrapper = styled.div`
    width: 24px;
    height: 24px;
    margin: 0 auto 12px;
`;

const FactTitle = styled.h3`
    font-family: "Poppins", sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.3;
    margin: 0 0 8px;
`;

const FactDescription = styled.p`
    font-family: "Poppins", sans-serif;
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
`;

const hasText = (value) => typeof value === "string" && value.trim().length > 0;

const getApiIconUrl = (iconField) => {
    if (!iconField) {
        return null;
    }

    if (typeof iconField === "string" && iconField.trim().length > 0) {
        return iconField;
    }

    if (typeof iconField?.url === "string" && iconField.url.trim().length > 0) {
        return iconField.url;
    }

    return null;
};

export default function FactsSection({
    facts,
    cityName,
    factFirstIcon,
    factSecondIcon,
    factThirdIcon,
}) {
    const cityLabel = hasText(cityName) ? cityName : "the city";

    const cards = [
        {
            title: hasText(facts?.firstTitle) ? facts.firstTitle : "Locally Curated Menu",
            description: hasText(facts?.firstDescription)
                ? facts.firstDescription
                : `Designed by locals in ${cityLabel} to showcase the most authentic local dishes`,
            iconSrc: getApiIconUrl(factFirstIcon) || FACT_DISH_ICON,
            iconAlt: factFirstIcon?.alt ?? factFirstIcon?.name ?? "Locally curated menu icon",
        },
        {
            title: hasText(facts?.secondTitle) ? facts.secondTitle : `Discover ${cityLabel} Secrets`,
            description: hasText(facts?.secondDescription)
                ? facts.secondDescription
                : "A local specialty or exclusive location, only revealed on the tour",
            iconSrc: getApiIconUrl(factSecondIcon) || FACT_CARD_ICON,
            iconAlt: factSecondIcon?.alt ?? factSecondIcon?.name ?? "Discover secrets icon",
        },
        {
            title: hasText(facts?.thirdTitle) ? facts.thirdTitle : `#1 Food Tour in ${cityLabel}`,
            description: hasText(facts?.thirdDescription)
                ? facts.thirdDescription
                : `Over 136,000 5-star Reviews; sharing the best food of ${cityLabel} - since 2013`,
            iconSrc: getApiIconUrl(factThirdIcon) || FACT_STAR_ICON,
            iconAlt: factThirdIcon?.alt ?? factThirdIcon?.name ?? "Top rated food tours icon",
        },
    ];

    return (
        <SectionWrapper>
            <Heading>{facts?.title ? facts.title.replaceAll('{city}', cityLabel) : `Why join our ${cityLabel} food tour?`}</Heading>
            <FactsGrid>
                {cards.map((card, index) => (
                    <FactCard key={index}>
                        <IconWrapper>
                            <Image src={card.iconSrc} alt={card.iconAlt} width={24} height={24} />
                        </IconWrapper>
                        <FactTitle>{card.title}</FactTitle>
                        <FactDescription>{card.description}</FactDescription>
                    </FactCard>
                ))}
            </FactsGrid>
        </SectionWrapper>
    );
}
